import React, { useState, useEffect, useMemo } from 'react';
import { Form } from '@mairistem/composants';
import { Categorie, ClassificationModal } from '@mairistem/comptabilite';
import {
  isEmpty, split, isEqual, find, map,
} from 'lodash';
import { Combobox, getErrorMessage } from '@mairistem/finances-utils';
import { toast } from 'react-toastify';
import { getCategories, postCategorie } from '../../../services/tiers/categorie';

interface ClassificationInputProps {
  identifiantCategorie: number;
  onChange: (data: Categorie) => void;
}

const ClassificationInput = ({
  identifiantCategorie,
  onChange,
  ...rest
}: ClassificationInputProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCategorie, setSelectedCategorie] = useState('');
  const [newLibelle, setNewLibelle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<Categorie[]>([]);

  // Chargement des catégories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await getCategories(searchQuery);
        setCategories(response.data);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Erreur lors de la récupération des catégories.'));
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [searchQuery]);

  // Sélection de la catégorie initiale
  useEffect(() => {
    if (identifiantCategorie) {
      const categorie = find(categories, (c) => c.identifiant === identifiantCategorie);
      if (categorie) {
        setSelectedCategorie(`${categorie.identifiant}-${categorie.libelle}`);
      }
    }
  }, [identifiantCategorie, categories]);

  const handleItemSelect = (_, { value }) => {
    if (!isEmpty(value)) {
      if (value === 'add') {
        setOpen(true);
        setNewLibelle('');
        return;
      }

      const [identifiant, libelle] = split(value, '-');
      setSelectedCategorie(value);

      onChange({
        identifiant: Number(identifiant),
        libelle,
      });
    } else {
      setSelectedCategorie('');
      setSearchQuery('');
      onChange({
        identifiant: null,
        libelle: '',
      });
    }
  };

  const handleSearchChange = (_, { searchQuery: query }) => setSearchQuery(query);

  const handleAddItem = (_, { value }) => {
    setOpen(true);
    setNewLibelle(isEqual(value, 'add') ? '' : value);
  };

  const handleSaveModal = async (_, categorie: Categorie) => {
    setLoading(true);
    postCategorie(categorie).then((newCategory: Categorie) => {
      onChange(newCategory);
      setSelectedCategorie(`${newCategory.identifiant}-${newCategory.libelle}`);
      setCategories((prevCategories) => [...prevCategories, newCategory]);
    }).catch((error) => {
      toast.error(getErrorMessage(error, 'Erreur lors de la création de la catégorie.'));
    }).finally(() => {
      setLoading(false);
      setOpen(false);
      setSearchQuery('');
    });
  };

  const options = useMemo(() => {
    const categoryOptions = map(categories, (c) => ({
      key: `${c.libelle}-${c.identifiant}`,
      value: `${c.identifiant}-${c.libelle}`,
      text: c.libelle,
    }));

    return isEmpty(searchQuery)
      ? [{ key: 'add', value: 'add', text: 'Ajouter une classification' }, ...categoryOptions]
      : categoryOptions;
  }, [categories, searchQuery]);

  return (
    <>
      <Form.Field
        {...rest}
        control={Combobox}
        // @ts-expect-error
        id="Identification-categorie"
        label="Classification"
        name="categorie"
        placeholder="Classification"
        clearable
        allowAdditions
        additionLabel="Ajouter "
        noResultsMessage="Aucune classification trouvée."
        onAddItem={handleAddItem}
        onChange={handleItemSelect}
        onSearchChange={handleSearchChange}
        onClose={() => setSearchQuery('')}
        options={options}
        search
        selection
        scrolling
        selectOnNavigation={false}
        value={selectedCategorie}
        loading={loading}
      />
      {open && (
        <ClassificationModal
          categorie={{ libelle: newLibelle }}
          onSave={handleSaveModal}
          onCancel={() => setOpen(false)}
          loading={loading}
        />
      )}
    </>
  );
};

export default ClassificationInput;
